import {
  differenceInCalendarDays,
  endOfDay,
  isAfter,
  startOfDay,
  subDays,
} from "date-fns";
import type { DateRange } from "@/hooks/useDateRange";

export function getAnalyticsMaxSelectableDate(): Date {
  return endOfDay(new Date());
}

export function isAfterAnalyticsMaxDate(date: Date, maxDate?: Date): boolean {
  const limit = startOfDay(maxDate ?? getAnalyticsMaxSelectableDate());
  return isAfter(startOfDay(date), limit);
}

/** Clamps analytics range ends to today; ensures from <= to when both set. */
export function clampAnalyticsDateRange(
  range: DateRange,
  maxDate: Date = getAnalyticsMaxSelectableDate(),
): DateRange {
  const maxEnd = endOfDay(maxDate);
  let from = range.from;
  let to = range.to;

  if (from && isAfterAnalyticsMaxDate(from, maxDate)) {
    from = maxEnd;
  }
  if (to && isAfterAnalyticsMaxDate(to, maxDate)) {
    to = maxEnd;
  }
  if (from && to && from > to) {
    to = from;
  }

  return { from, to };
}

/** Calendar date as YYYY-MM-DD in local timezone (avoids UTC shift from toISOString). */
export function formatLocalDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function parseLocalDateParam(value: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return undefined;
  }

  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(year, month - 1, day);

  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed;
}

export function toAnalyticsDateParam(
  date: Date | undefined,
): string | undefined {
  if (!date || Number.isNaN(date.getTime())) {
    return undefined;
  }

  return formatLocalDateParam(date);
}

export function getDefaultAnalyticsDateRange(): DateRange {
  const today = new Date();
  return {
    from: startOfDay(subDays(today, 29)),
    to: endOfDay(today),
  };
}

/** Previous period of the same length, ending the day before `dateRange.from`. */
export function getPreviousAnalyticsDateRange(
  dateRange: DateRange,
): DateRange | null {
  if (!dateRange.from || !dateRange.to) {
    return null;
  }

  const inclusiveDays =
    differenceInCalendarDays(dateRange.to, dateRange.from) + 1;
  const previousTo = endOfDay(subDays(startOfDay(dateRange.from), 1));
  const previousFrom = startOfDay(subDays(previousTo, inclusiveDays - 1));

  return { from: previousFrom, to: previousTo };
}
